import { createI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';

/**
 * Règle de pluriel polonaise : trois formes, là où le français et l'anglais en ont deux.
 *
 *   forme 1 : la valeur vaut exactement 1                      -> 1 maszyna
 *   forme 2 : finit par 2, 3 ou 4, SAUF 12, 13, 14             -> 2 maszyny, 23 maszyny
 *   forme 3 : tous les autres cas, zéro compris                -> 0 maszyn, 5 maszyn, 12 maszyn
 *
 * Les valeurs 12 à 14 et 112 à 114 sont le piège : la règle ne se résume pas au dernier
 * chiffre, il faut aussi regarder les deux derniers.
 *
 * Repli pour un message qui ne porterait que deux formes : singulier / pluriel.
 */
export function polishPluralIndex(choice, choicesLength) {
  if (choicesLength < 3) {
    return choice === 1 ? 0 : choicesLength - 1;
  }
  if (choice === 1) {
    return 0;
  }
  const lastDigit = choice % 10;
  const lastTwoDigits = choice % 100;
  const isTeen = lastTwoDigits >= 12 && lastTwoDigits <= 14;

  return (lastDigit >= 2 && lastDigit <= 4 && !isTeen) ? 1 : 2;
}

/**
 * Règle de pluriel française : zéro prend le singulier.
 *
 *   0 minute, 1 minute, 2 minutes
 *
 * vue-i18n rend zéro au pluriel par défaut, ce qui est faux en français. Sans cette règle,
 * chaque composant devait forcer le compteur à 1 pour obtenir « 0 minute », contournement
 * qui rendait le libellé faux dans les langues où zéro prend une forme propre : le polonais
 * attend « 0 minut » et non « 0 minuta ». La règle appartient ici, pas aux composants.
 */
export function frenchPluralIndex(choice, choicesLength) {
  return choice <= 1 ? 0 : choicesLength - 1;
}

export const i18nOptions = {
  legacy: false,
  pluralRules: {
    fr: frenchPluralIndex,
    pl: polishPluralIndex,
  },
  locale: navigator.language.split('-')[0], // Default locale is the browser language
  fallbackLocale: 'en',
  messages: {},
  missing: (locale, key) => {
    // Report to Sentry but return nothing: returning captureMessage()'s event id
    // would make vue-i18n render that id instead of falling back to the key.
    Sentry.captureMessage(`Missing translation ${key} in locale ${locale}`);
  },
  globalInjection: true,
  warnHtmlMessage: false,
};

const i18n = createI18n(i18nOptions);

async function loadMessages(locale) {
  let localeToLoad = locale;
  let messages;
  if (localeToLoad === 'auto') {
    // eslint-disable-next-line prefer-destructuring
    localeToLoad = navigator.languages[0].split('-')[0];
  }

  // Check translate file exists other fallback
  if (Object.keys(i18n.global.getLocaleMessage(localeToLoad)).length === 0) {
    const babelEditFile = localeToLoad.replace('_', '-');
    try {
      messages = await import(`../lang/${babelEditFile}.json`);
    } catch {
      messages = await import(`../lang/${i18n.global.fallbackLocale.value}.json`);
      localeToLoad = i18n.global.fallbackLocale.value;
    }
    i18n.global.setLocaleMessage(localeToLoad, messages.default);
  }
  i18n.global.locale.value = localeToLoad;
}

export { i18n, loadMessages };
